import { Await, createFileRoute, defer } from "@tanstack/react-router";
import { Suspense } from "react";
import { SiteNav } from "@/components/site/SiteNav";
import { Hero } from "@/components/landing/Hero";
import { Services } from "@/components/landing/Services";
import { Sectors } from "@/components/landing/Sectors";
import { Process } from "@/components/landing/Process";
import { Coverage } from "@/components/landing/Coverage";
import { FAQ } from "@/components/landing/FAQ";
import { BlogPreview } from "@/components/landing/BlogPreview";
import { FinalCTA } from "@/components/landing/FinalCTA";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE, SITE_URL } from "@/lib/site";
import { seoMeta, canonicalLink, robotsMeta } from "@/lib/seo";
import { faqJsonLd } from "@/components/site/FAQAccordion";
import heroImg from "@/assets/hero-person.jpg";
import { contenido as contenidoHome } from "@/content/pages/home";
import { vistaHome } from "@/lib/page-content/vista";
import { getLatestPublishedPosts } from "@/server/blog/blog-public.fns";

const DEFAULT_TITLE = "Agencia BTL en Lima y Perú: Activaciones y Eventos | Improve Yourself";
const DEFAULT_DESCRIPTION =
  "Agencia y empresa BTL en Lima y todo el Perú: activaciones de marca, eventos corporativos, trade marketing, impulsadoras y sampling. Propuesta en 72 horas.";

export const Route = createFileRoute("/")({
  loader: () => {
    const postsPromise = defer(getLatestPublishedPosts().catch(() => []));
    return { postsPromise };
  },
  component: Index,
  head: ({ match }) => {
    const override = match.context.seoOverride;
    const vista = vistaHome(contenidoHome, match.context.pageContent);
    // El slot "hero-home" (specs/111-slots-imagenes-sitio) sobreescribe también
    // el preload: si no coincidiera con lo que Hero.tsx realmente renderiza,
    // el navegador precargaría una imagen que no se usa (doble descarga, peor
    // LCP — justo lo que esta feature busca evitar, ver research.md Decisión 4).
    const heroSlot = match.context.siteImageSlots?.["hero-home"];
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/",
          image: override?.ogImage ?? heroSlot?.src ?? heroImg,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: [
        ...canonicalLink(override?.canonicalPath ?? "/"),
        {
          rel: "preload",
          as: "image",
          href: heroSlot?.src ?? "/assets/hero-person-350w.webp",
          imageSrcSet:
            heroSlot?.srcSet ??
            "/assets/hero-person-300w.webp 300w, /assets/hero-person-350w.webp 350w, /assets/hero-person-500w.webp 500w, /assets/hero-person-700w.webp 700w",
          imageSizes: "(min-width: 768px) 450px, 90vw",
          type: "image/webp",
          fetchPriority: "high",
        },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": `${SITE_URL}/#website`,
            name: SITE.name,
            url: SITE_URL,
            inLanguage: "es-PE",
            publisher: {
              "@type": "LocalBusiness",
              "@id": `${SITE_URL}/#organization`,
            },
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: `${SITE_URL}/blog?q={search_term_string}`,
              },
              "query-input": "required name=search_term_string",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(faqJsonLd(vista.faqs.map((f) => ({ q: f.q, a: f.a.texto })))),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "@id": `${SITE_URL}/#proceso`,
            name: "Cómo trabajamos contigo",
            description:
              "Proceso de trabajo de Improve Yourself para activaciones BTL, eventos corporativos y campañas de marketing promocional en Lima.",
            step: vista.proceso.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.title,
              text: s.desc.texto,
            })),
          }),
        },
      ],
    };
  },
});

function Index() {
  const { postsPromise } = Route.useLoaderData();
  const { siteImageSlots, pageContent } = Route.useRouteContext();
  const vista = vistaHome(contenidoHome, pageContent);
  return (
    <div className="min-h-screen bg-background">
      <SiteNav />
      <main id="main-content">
        <Hero heroOverride={siteImageSlots?.["hero-home"]} intro={vista.intro} />
        <Services serviceOverrides={siteImageSlots ?? undefined} />
        <Sectors
          sectorOverrides={
            siteImageSlots
              ? {
                  "sector-1": siteImageSlots["sector-1"],
                  "sector-2": siteImageSlots["sector-2"],
                  "sector-3": siteImageSlots["sector-3"],
                  "sector-4": siteImageSlots["sector-4"],
                }
              : undefined
          }
        />
        <Process steps={vista.proceso} />
        <Coverage />
        <FAQ faqs={vista.faqs} />
        <Suspense fallback={null}>
          <Await promise={postsPromise}>{(posts) => <BlogPreview posts={posts} />}</Await>
        </Suspense>
        <FinalCTA />
      </main>
      <SiteFooter />
    </div>
  );
}
